import { useSubscription } from '@apollo/client'
import { captureException, setUser as setSentryUser } from '@sentry/react'
import Gleap from 'gleap'
import includes from 'lodash/includes'
import {
    Fragment,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { useMediaQuery } from 'react-responsive'
import { Route, useHistory, useLocation } from 'react-router-dom'

import { FlagsProvider } from 'src/components/common/FeatureFlags'
import Navigator from 'src/components/common/Navigator'
import Header from 'src/components/common/Navigator/header/header'
import PageContentWrapper from 'src/components/common/PageContentWrapper'
import Button from 'src/components/common/button'
import Modal from 'src/components/common/modal'
import openNotification from 'src/components/common/notification'
import Spinner from 'src/components/common/spinner'
import BuildContext from 'src/context/buildContext'
import { DummyDataContextProvider } from 'src/context/dummyData'
import { ModuleNavProvider } from 'src/context/module-nav-context'
import { NavigatorContextProvider } from 'src/context/navigatorContext'
import { useOrgSize } from 'src/context/org-size-ctx'
import { PermissionsContextProvider } from 'src/context/permissionsContext'
import ScreenContext from 'src/context/screenContext'
import UserDetailsContext from 'src/context/userDetailsContext'
import UserEmailContext from 'src/context/userEmailContext'
import routes from 'src/routes'
import {
    GET_FEATURE_FLAGS,
    USER_DETAILS_FOR_CONTEXT,
} from 'src/schema/subscriptions'
import useIdleTimeout from 'src/utils/hooks/useIdleTimeout'
import useLogout from 'src/utils/hooks/useLogout'
import logger from 'src/utils/logger'

import SimulationModeSwitcher from './simulation-mode-switcher'

const IDLE_TIMEOUT = 60 // minutes

const PrivateRoute = ({
    component: Component,
    // eslint-disable-next-line no-unused-vars
    hasMobileView = false,
    ...rest
}) => {
    const logout = useLogout()

    const history = useHistory()

    const { hasOrgSize, loading: orgSizeLoading } = useOrgSize()

    useEffect(() => {
        if (orgSizeLoading) {
            return
        }

        if (!hasOrgSize) {
            history.replace(routes.settings)
        }
    }, [hasOrgSize, history, orgSizeLoading])

    const [authnError, setAuthnError] = useState(false)

    /*
     * Show session timeout confirmation modal
     */
    const [showWarningModal, setShowWarningModal] = useState(false)

    /*
     * Callback for when the inactivity period has passed
     */
    const handleOnIdle = useCallback(() => {
        setShowWarningModal(false)
        if (logout && typeof logout === 'function') {
            openNotification('success', 'Logging you out..', 2)
            setTimeout(() => {
                logout()
            }, 1.5 * 1000)
        }
    }, [logout])

    /*
     * Callback for when warning period has passed
     */
    const handleOnWarning = useCallback(() => {
        setShowWarningModal(true)
    }, [])

    const { idleTimer } = useIdleTimeout({
        onWarning: handleOnWarning,
        onIdle: handleOnIdle,
        idleTime: IDLE_TIMEOUT,
    })

    /*
     * Reset the the timeout
     */
    const onStay = () => {
        setShowWarningModal(false)
        idleTimer.reset()
    }

    // media query helper
    const { SHOW_RELOAD_BANNER } = useContext(BuildContext)
    const { userEmail } = useContext(UserEmailContext)

    const [navbar, setNavbar] = useState()

    const isSmallScreen = useMediaQuery({ maxWidth: '1024px' })

    const {
        loading: loadingUserDetails,
        data: { users } = {},
        error: userDetailsError,
    } = useSubscription(USER_DETAILS_FOR_CONTEXT, {
        onError: (error) => {
            if (
                error.message ===
                    'Authentication hook unauthorized this request' ||
                error.message === 'Invalid response from authorization hook'
            ) {
                setAuthnError(true)
                if (logout && typeof logout === 'function') {
                    openNotification('success', 'Logging you out..', 2)
                    setTimeout(() => {
                        logout()
                    }, 1.5 * 1000)
                }
            } else {
                captureException(error, {
                    extra: {
                        message:
                            'an error occurred while fetching user details',
                    },
                })
                logger.error(error)
            }
        },
        variables: {
            email: userEmail,
        },
    })

    const { data: { features } = {} } = useSubscription(GET_FEATURE_FLAGS, {
        onError: (error) => {
            if (
                error.message ===
                    'Authentication hook unauthorized this request' ||
                error.message === 'Invalid response from authorization hook'
            ) {
                setAuthnError(true)
                if (logout && typeof logout === 'function') {
                    openNotification('success', 'Logging you out..', 2)
                    setTimeout(() => {
                        logout()
                    }, 1.5 * 1000)
                }
            } else {
                captureException(error, {
                    extra: {
                        message:
                            'an error occurred while fetching feature flags',
                    },
                })
                logger.error(error)
            }
        },
    })

    const currentUser = useMemo(() => {
        return users?.[0]
    }, [users])

    const { pathname } = useLocation()

    useEffect(() => {
        // prevent scroll top for nested routes
        if (
            includes(
                [
                    routes.alertsByRules,
                    routes.alertsByAssets,
                    routes.alertsByGroups,
                ],
                pathname
            )
        )
            return

        window.scrollTo({
            top: 0,
            left: 0,
        })
    }, [pathname])

    useEffect(() => {
        if (!currentUser) {
            return
        }

        setSentryUser({ email: currentUser.email, id: currentUser.id })
        Gleap.identify(currentUser.id, {
            email: currentUser.email,
            name: currentUser.firstName
                .concat(' ')
                .concat(currentUser.lastName),
            companyId: currentUser.organization.id,
            companyName: currentUser.organization.legalName,
            plan: JSON.stringify(currentUser.organization.planDetails),
        })
    }, [currentUser])

    useEffect(() => {
        if (isSmallScreen) {
            //TODO: hide gleap widget
            // const Intercom = window.Intercom
            // Intercom('update', {
            //     hide_default_launcher: true,
            // })
        } else {
            //TODO: show gleap widget
            // const Intercom = window.Intercom
            // Intercom('update', {
            //     hide_default_launcher: false,
            // })
        }
    }, [isSmallScreen])

    if (loadingUserDetails) {
        return <Spinner />
    }

    if (authnError) {
        return (
            <div className="flex h-screen w-screen items-center justify-center text-center text-3xl">
                <Spinner
                    loadingText={
                        <h3 className="text-lg font-bold text-primary-red-500">
                            Your session has expired, logging you out...
                        </h3>
                    }
                />
            </div>
        )
    }

    if (userDetailsError) {
        return (
            <div className="flex h-screen w-screen items-center justify-center text-center text-3xl">
                <h1>
                    Whoops we didn't expect this, please try reloading this page
                </h1>
            </div>
        )
    }

    if (currentUser.organization.isRevoked) {
        return (
            <div className="flex h-screen w-screen items-center justify-center text-center text-3xl">
                <h1>
                    Your Account's Access has been Revoked. Please contact your
                    Organization Administrator
                </h1>
            </div>
        )
    }

    return (
        <>
            <FlagsProvider
                features={features?.map((feature) => feature.feature)}
            >
                <SimulationModeSwitcher />
                <DummyDataContextProvider>
                    <UserDetailsContext.Provider
                        value={{
                            userDetails: currentUser,
                        }}
                    >
                        <PermissionsContextProvider>
                            <ScreenContext.Provider value={{ isSmallScreen }}>
                                <NavigatorContextProvider>
                                    <ModuleNavProvider
                                        navbar={navbar}
                                        setNavbar={setNavbar}
                                    >
                                        <Header />
                                        <div className="flex h-screen w-full overflow-x-hidden">
                                            <Navigator />
                                            <PageContentWrapper
                                                isSmallScreen={isSmallScreen}
                                                shouldReload={
                                                    SHOW_RELOAD_BANNER
                                                }
                                            >
                                                <Route
                                                    {...rest}
                                                    render={(props) => (
                                                        <Fragment>
                                                            <Component
                                                                {...props}
                                                                userDetails={
                                                                    currentUser
                                                                }
                                                            />
                                                        </Fragment>
                                                    )}
                                                />
                                            </PageContentWrapper>
                                            <Modal
                                                bodyStyle={{
                                                    background: '#2F3B52',
                                                }}
                                                visible={showWarningModal}
                                                width={'450px'}
                                                closable={false}
                                            >
                                                <div className="mt-0">
                                                    <h2 className="text-base text-neutral-gray-75 2xl:text-lg">
                                                        Session Timeout
                                                    </h2>

                                                    <p className="mt-4 text-sm text-white 2xl:text-base">
                                                        You are about to be
                                                        logged out as you have
                                                        been idle for 1 hour.
                                                        Click&nbsp;
                                                        <span className="uppercase text-primary-red-500">
                                                            stay
                                                        </span>
                                                        &nbsp;to extend your
                                                        session
                                                    </p>

                                                    <div className="mt-8 mb-2 text-right">
                                                        <Button
                                                            intent={'primary'}
                                                            extendClass="ml-2 w-[130px]"
                                                            label="STAY"
                                                            onClick={onStay}
                                                        />
                                                    </div>
                                                </div>
                                            </Modal>
                                        </div>
                                    </ModuleNavProvider>
                                </NavigatorContextProvider>
                            </ScreenContext.Provider>
                        </PermissionsContextProvider>
                    </UserDetailsContext.Provider>
                </DummyDataContextProvider>
            </FlagsProvider>
        </>
    )
}

export default PrivateRoute
